<template>
	<div class="modal-container ModalAddEditAddition">
		<div class="modal-header">
			<div>
				{{ additionOrAdjustmentLabel }} Details
				<div class="additional-info">
					<span v-if="$global.isAdminView && addition">(Title ID: {{ addition.titleId ?? 'N/A' }}, Original: {{ bucket.originalTitleId ?? 'N/A' }})</span>
				</div>
			</div>

			<EditInfo
				v-if="!isNewAddition"
				:editableObject="addition"
			/>
		</div>
		<div class="modal-body">
			<fieldset
				:disabled="modal.isModalDisabled && !isNewAdjustment"
				class="no-margin-grid"
				:style="$grid('1')"
			>
				<Panel title="Basic Info">
					<template #HeaderComponent>
						<div class="panel-buttons">
							<InputRichDropdown
								v-if="isNewAddition"
								v-model:saturn="selectedStoreAddition"
								:list="storeAdditions"
								:valueMap="(pf: any) => pf"
								:display="(pf: any) => pf.name"
								@change="onStoreAdditionSelected"
								:nothingSelected="`-- ${additionOrAdjustmentLabel} Type --`"
							>
							</InputRichDropdown>
						</div>
					</template>

					<div :style="$grid('1-1')">
						<div>
							<InputTextbox
								label="Name"
								v-model:saturn="addition.name"
								:placeholder="`${additionOrAdjustmentLabel} Name`"
								:isUpperCase="true"
								:invalid="v$.addition.name.$invalid"
								:disabled="!addition.isEditableName || (isNewAddition && !selectedStoreAddition) || (modal.isDealLocked && addition.itemized)"
							>
								<template #validation>
									<div v-if="v$.addition.name.required.$invalid">Name is required</div>
									<div v-if="v$.addition.name.uniqueAdditionName.$invalid">Name must be unique</div>
								</template>
							</InputTextbox>
						</div>
						<div>
							<InputRichDropdown
								label="Type"
								v-model:saturn="addition.type"
								:list="additionTypes"
								:valueMap="(additionType: any) => additionType.value"
								:display="(additionType: any) => additionType.display"
								nothingSelected="-- Select Value --"
								@change="onAdditionTypeSelected"
								:invalid="v$.addition.type.$invalid"
								:disabled="!addition.isEditableType || (isNewAddition && !selectedStoreAddition) || modal.isDealLocked"
							>
								<template #validation>
									<div v-if="v$.addition.type.required.$invalid">Type is required</div>
								</template>
							</InputRichDropdown>
						</div>
					</div>
					<div :style="$grid('1-1')">
						<InputCurrency
							label="Price"
							placeholder="Price"
							v-model:saturn="addition.price"
							:precision="2"
							:invalid="v$.addition.price.$invalid"
							:disabled="!addition.isEditablePrice || (isNewAddition && !selectedStoreAddition) || modal.isDealLocked"
						>
							<template #validation>
								<div v-if="v$.addition.price.required.$invalid">Price is required</div>
							</template>
						</InputCurrency>
						<InputCurrency
							label="Cost"
							placeholder="Cost"
							v-model:saturn="addition.cost"
							:precision="2"
							:invalid="v$.addition.cost.$invalid"
							:disabled="!addition.isEditableCost || (isNewAddition && !selectedStoreAddition)"
						>
							<template #validation>
								<div v-if="v$.addition.cost.required.$invalid">Cost is required</div>
							</template>
						</InputCurrency>
					</div>
					<div
						:style="$grid('1-1')"
						class="no-margin-grid"
					>
						<InputRichDropdown
							label="Profit Flag"
							v-model:saturn="addition.profitFlag"
							:list="profitFlags"
							:valueMap="(pf: any) => pf.value"
							:display="(pf: any) => pf.text"
							nothingSelected="-- Select Value --"
							:invalid="v$.addition.profitFlag.$invalid"
							:disabled="!addition.isEditableProfitFlag || (isNewAddition && !selectedStoreAddition) || modal.isDealLocked"
						>
							<template #validation>
								<div v-if="v$.addition.profitFlag.required.$invalid">Profit Flag To is required</div>
							</template>
						</InputRichDropdown>

						<InputRichDropdown
							v-if="!isNewAddition || selectedStoreAddition"
							label="Itemized on Buyer's Order"
							class="itemized"
							v-model:saturn="addition.itemized"
							:list="ITEMIZED_OPTIONS"
							:valueMap="(o: any) => o.value"
							:display="(o: any) => o.display"
							:disabled="!addition.isEditableItemized || (addition.isEditableItemized && modal.isDealLocked)"
						>
						</InputRichDropdown>

						<!--PhysicalAddition-->
						<fieldset
							v-if="addition.physicalAddition"
							:disabled="modal.isDealLocked"
						>
							<div
								class="radio-button-inline"
								:invalid="v$.addition.physicalAddition.isInstalled.$invalid"
							>
								<div>Is this item already installed?</div>
								<div>
									<InputRadio
										label="Yes"
										:valueMatch="true"
										v-model="addition.physicalAddition.isInstalled"
									></InputRadio>
									<InputRadio
										label="No"
										:valueMatch="false"
										v-model="addition.physicalAddition.isInstalled"
									></InputRadio>
								</div>
							</div>
						</fieldset>

						<fieldset
							v-if="addition.monetaryAddition"
							:disabled="modal.isDealLocked"
						>
							<InputTextbox
								label="Paid To"
								v-model:saturn="addition.monetaryAddition.paidTo"
								placeholder="Paid To"
								:isUpperCase="true"
								:invalid="v$.addition.monetaryAddition.paidTo.$invalid"
							>
								<template #validation>
									<div v-if="v$.addition.monetaryAddition.paidTo.required.$invalid">Paid To is required</div>
								</template>
							</InputTextbox>
						</fieldset>
					</div>
				</Panel>
				<Panel
					v-if="!isAdjustment"
					title="Tax Lines to Apply"
				>
					<fieldset
						class="no-margin-grid"
						:style="$grid('1')"
						:disabled="modal.isDealLocked"
					>
						<div
							class="no-margin-grid"
							:style="$grid('5-1')"
							v-for="(taxLine, taxLineIndex) in bo.taxGroups"
							:key="taxLine.name"
						>
							<InputCheckbox
								:modelValue="isInThisTaxLine(taxLineIndex)"
								@click="toggleTaxLine(taxLineIndex)"
								class="panel-header-collapse-button description"
								:label="taxLine.name"
							></InputCheckbox>

							<div class="amt">
								<InputNumber
									v-model:saturn="taxLine.rate"
									placeholder="Tax Rate"
									:precision="2"
									:isPercent="true"
									:disabled="true"
								>
								</InputNumber>
							</div>
						</div>
					</fieldset>
				</Panel>
			</fieldset>
		</div>
		<div class="modal-footer modal-footer-split">
			<div>
				<button
					v-if="!isNewAddition"
					class="button-unwind"
					@click="deleteAddition()"
					:disabled="((modal.isDealLocked && Boolean(addition.price || addition.itemized)) || modal.isModalDisabled) && !isAdjustment"
				>
					Delete {{ additionOrAdjustmentLabel }}
				</button>
			</div>
			<div>
				<button
					class="button-unwind"
					@click="cancel()"
				>
					Cancel
				</button>
				<button
					class="button-save"
					:disabled="(modal.isModalDisabled && !isNewAdjustment) || v$.$invalid || (modal.isDealLocked && addition.itemized && addition.isEditableItemized)"
					@click="close()"
				>
					Save
				</button>
			</div>
		</div>

		<IsBusyScreenComponent v-if="isBusy" />
	</div>
</template>
<script lang="ts">
	export interface IAdditionsBucketModalProps extends IBucketModalProps {
		storeAdditions: StoreAddition[];
		fimenu: FIMenu;
	}
</script>

<script setup lang="ts">
	import { computed, Ref, ref, watch } from 'vue';
	import $modal from '@core/services/modal';
	import auth from '@core/services/auth';
	import DropdownHelper from '@core/helpers/dropdown-helper';
	import { useVuelidate } from '@vuelidate/core';
	import util from '@core/services/util';

	import AccountingHelper from '@/helpers/AccountingHelper';
	import { ADDITION_TYPES } from '@core/classes/Enums';
	import DealAddition from '@core/classes/Deal/DealAddition';
	import FIMenu from '@core/classes/FIMenu';
	import FIMenuBuyersOrder from '@core/classes/FIMenuBuyersOrder';
	import FIMenuBuyersOrderBucket from '@core/classes/FIMenuBuyersOrderBucket';
	import FIMenuBuyersOrderBucketItem from '@core/classes/FIMenuBuyersOrderBucketItem';
	import { IBucketModalProps } from '@core/_interfaces/ComponentProps';
	import MonetaryAddition from '@core/classes/Deal/MonetaryAddition';
	import PhysicalAddition from '@core/classes/Deal/PhysicalAddition';
	import { StoreAddition } from '@core/classes/Store/StoreAddition';

	import AccountingTransactionMapper from '@/components/accounting/AccountingTransactionMapper.vue';
	import EditInfo from '@/components/EditInfo.vue';
	import InputCheckbox from '@core/components/InputCheckbox.vue';
	import InputCurrency from '@core/components/InputCurrency.vue';
	import InputNumber from '@core/components/InputNumber.vue';
	import InputRadio from '@core/components/InputRadio.vue';
	import InputRichDropdown from '@core/components/InputRichDropdown.vue';
	import InputTextbox from '@core/components/InputTextbox.vue';
	import IsBusyScreenComponent from '@core/components/IsBusyScreenComponent.vue';
	import Panel from '@core/components/Panel.vue';

	const { modal } = defineProps<{
		modal: IAdditionsBucketModalProps;
	}>();

	const user = auth.getTokenPayload();

	// const ADDITIONS_TITLE_ID = '10001';
	const ITEMIZED_OPTIONS = [
		{
			value: true,
			display: 'Yes',
		},
		{
			value: false,
			display: 'No',
		},
	];
	const additionTypes = DropdownHelper.getAdditionTypes();

	// Copy Buyer's Order
	const bo = ref<FIMenuBuyersOrder>(new FIMenuBuyersOrder(modal.bo));
	const accountingHelper = new AccountingHelper();
	const fimenu = ref<FIMenu>(modal.fimenu) as Ref<FIMenu>;
	const addition = ref<DealAddition>(null);
	const additionIndex = ref<number>(null);
	const bucket = ref<FIMenuBuyersOrderBucket>(modal.bucketContent.parentBucket);
	const originalName = ref<string>(null);
	const isNewAddition = ref(!modal.bucketContent.bucketItem);
	const selectedStoreAddition = ref<StoreAddition>(null);
	const storeAdditions = ref<StoreAddition[]>([]);
	const profitFlags = ref(DropdownHelper.getProfitFlags());
	const isBusy = ref(false);

	//IF THE DEAL IS FINALIZED, WE NEED TO CREATE AN ADJUSTMENT
	const isAdjustment = computed(() => bucket.value.bucketType === 'Adjustments');
	const isNewAdjustment = computed(() => isNewAddition.value && isAdjustment.value);

	const additionOrAdjustmentLabel = computed(() => (isAdjustment.value ? 'Adjustment' : 'Addition'));

	//WE ONLY WANT TO ALLOW TO ADD THE SAME ADDITION ONCE, NAME MUST BE UNIQUE
	//WE ARE NOT ALLOWING CUSTOM ADDITIONS DUE TO ACCOUNTING MAPPING LOGIC
	//let allAdditionNamesAlreadyUsed = bo.additions.map(a => a.name);
	//storeAdditions = modal.storeAdditions.filter(addition => !allAdditionNamesAlreadyUsed.includes(addition.name)).map(addition => new StoreAddition(addition));

	storeAdditions.value = modal.storeAdditions.map(addition => new StoreAddition(addition));

	//TO ALLOW CUSTOM ADDITIONS
	// storeAdditions.value.push(getNewStoreAddition())

	//IF NEW ADDITION, CREATE IT AND CREATE A BUCKET FOR IT
	if (isNewAddition.value) {
		//CREATE NEW ADDITION AND NEW BUCKET
		// let foundBucket = null;
		// if (!modal.bucketContent?.parentBucket) foundBucket = bo.value.addNewDefaultBucket(modal.bucketContent.parentBucket.bucketType, 0, 0, null, null, ADDITIONS_TITLE_ID);

		addition.value = bo.value.addNewDefaultAddition(modal.bucketContent.parentBucket.titleId, true, true, true);
	} else {
		// Find corresponding bucket in Buyer's Order copy
		bucket.value = bo.value.buckets.find(
			b =>
				b.bucketType === modal.bucketContent.parentBucket.bucketType &&
				b.originalTitleId === modal.bucketContent.parentBucket.originalTitleId &&
				b.titleId === modal.bucketContent.parentBucket.titleId &&
				b.taxIndex === modal.bucketContent.parentBucket.taxIndex &&
				b.upfrontTaxIndex === modal.bucketContent.parentBucket.upfrontTaxIndex,
		);

		addition.value = bo.value.additions.find((addition: DealAddition, index) => {
			return addition.name == modal.bucketContent.bucketItem.name;
		});
		originalName.value = addition.value.name;

		if (!bucket.value) {
			bucket.value = bo.value.addNewDefaultBucket(bucket.value.bucketType, 0, 0, null, addition.value.titleId, addition.value.titleId);
		}

		additionIndex.value = bo.value.additions.findIndex((addition: DealAddition) => addition.name == modal.bucketContent.bucketItem.name);
	}

	// function getNewStoreAddition() {
	//     //STORE DEFAULT ADDITION
	//     const customStoreAddition = new StoreAddition();
	//     customStoreAddition.isEditableName = true;
	//     customStoreAddition.isEditableCost = true;
	//     customStoreAddition.isEditablePrice = true;
	//     customStoreAddition.isEditableType = true;
	//     customStoreAddition.isEditableProfitFlag = true;
	//     customStoreAddition.isEditableTitleId = true;
	//     customStoreAddition.profitFlag = PROFIT_FLAGS.defaultForAdditions();
	//     customStoreAddition.titleId = ADDITIONS_TITLE_ID;

	//     return customStoreAddition;
	// }

	function onStoreAdditionSelected() {
		if (!selectedStoreAddition.value) return;

		Object.assign(addition.value, selectedStoreAddition.value);

		addition.value.itemized = addition.value.isEditableItemized ? false : selectedStoreAddition.value.itemized;

		if (selectedStoreAddition.value.clearDescriptionOnSelection) {
			addition.value.name = null;
		}

		//SET UP BUCKET & TITLE ID
		let newBucket = null;
		if (selectedStoreAddition.value.titleId) {
			newBucket = bo.value.buckets.find(
				(currentBucket: FIMenuBuyersOrderBucket) =>
					currentBucket.bucketType === modal.bucketContent.parentBucket.bucketType && currentBucket.titleId == selectedStoreAddition.value.titleId,
			);
		}

		if (!newBucket) {
			newBucket = bo.value.addNewDefaultBucket(modal.bucketContent.parentBucket.bucketType, 0, 0, null, null, selectedStoreAddition.value.titleId);
		}

		bucket.value = newBucket;

		// Adjustments are not taxable, so lets clear the tax lines
		if (isNewAdjustment.value) {
			clearTaxLines();
		}
	}

	//GIVEN A taxLineIndex RETURNS TRUE IF THE CURRENT ITEM'S IS TAXED ON THAT TAX LINE
	function isInThisTaxLine(taxLineIndex: number) {
		const taxIndex = bo.value.isLease() ? bucket.value.upfrontTaxIndex : bucket.value.taxIndex; //Which tax index? (check if the bucket is upfront)
		const taxBinaryArray = util.taxIndexToBinaryArray(taxIndex);
		const isInThisTaxLine = taxBinaryArray[taxLineIndex] == '1';

		return isInThisTaxLine;
	}

	function toggleTaxLine(taxLineIndex: number) {
		const isSelected = !isInThisTaxLine(taxLineIndex);

		const originalBucketData = new FIMenuBuyersOrderBucket(bucket.value);

		//GET THE NEW TAX INDEX THIS ITEM WILL HAVE AFTER TOGGLE
		const taxBinaryArray = util.taxIndexToBinaryArray(bucket.value.taxIndex);
		taxBinaryArray[taxLineIndex] = isSelected ? '1' : '0';
		const newTaxIndex = util.binaryArrayToTaxIndex(taxBinaryArray);

		//GET THE NEW TAX INDEX THIS ITEM WILL HAVE AFTER TOGGLE
		const upfrontTaxBinaryArray = util.taxIndexToBinaryArray(bucket.value.upfrontTaxIndex);
		upfrontTaxBinaryArray[taxLineIndex] = isSelected ? '1' : '0';
		const newUpfrontTaxIndex = util.binaryArrayToTaxIndex(upfrontTaxBinaryArray);

		//CREATE NEW BUCKET WITH THE NEW TAX INDEX
		bucket.value = bo.value.addNewDefaultBucket(
			modal.bucketContent.parentBucket.bucketType,
			newTaxIndex,
			newUpfrontTaxIndex,
			originalBucketData,
			null,
			bucket.value.originalTitleId,
		);
		addition.value.titleId = bucket.value.titleId;
	}

	// When called, sets current bucket to a new one with tax index 0
	function clearTaxLines() {
		const originalBucketData = new FIMenuBuyersOrderBucket(bucket.value);

		bucket.value = bo.value.addNewDefaultBucket(
			modal.bucketContent.parentBucket.bucketType,
			0, // sets tax index to 0
			0, // sets upfront tax index to 0
			originalBucketData,
			null,
			bucket.value.originalTitleId,
		);
		addition.value.titleId = bucket.value.titleId;
	}

	function onAdditionTypeSelected() {
		if (addition.value.type == ADDITION_TYPES.PHYSICAL_ADDITION) {
			addition.value.physicalAddition = new PhysicalAddition();
			addition.value.monetaryAddition = null;
		} else if (addition.value.type == ADDITION_TYPES.MONETARY_ADDITION) {
			addition.value.monetaryAddition = new MonetaryAddition();
			addition.value.physicalAddition = null;
		} else {
			addition.value.physicalAddition = null;
			addition.value.monetaryAddition = null;
		}
	}
	async function deleteAddition() {
		const result = confirm(`Are you sure you want to delete this ${additionOrAdjustmentLabel.value}?`);
		if (result) {
			const foundBucket = bo.value.buckets.find(bucket => bucket.items.some(item => item.name == originalName.value));
			if (!foundBucket) {
				util.toastr('error', 'Buyers Order', `Could not remove ${additionOrAdjustmentLabel.value}`);
				$modal.cancel();
			}

			bo.value.removeAddition(additionIndex.value);
			foundBucket.removeItem(originalName.value);
			// bucket.value.removeItem(originalName.value);

			if (isAdjustment.value) {
				isBusy.value = true;

				const successfullyDeleted = await accountingHelper.deleteAdditionAdjustment(addition.value);
				if (!successfullyDeleted) {
					$modal.cancel();
				}

				isBusy.value = false;
			}

			$modal.close(bo.value);
		}
	}
	async function close() {
		let foundItem = bucket.value.items.find((currentItem: FIMenuBuyersOrderBucketItem) => currentItem.name == originalName.value);
		if (foundItem) {
			foundItem.name = addition.value.name;
			foundItem.amount = addition.value.price;
			foundItem.cost = addition.value.cost;
		} else {
			foundItem = new FIMenuBuyersOrderBucketItem({
				bucketItem: {
					amount: addition.value.price,
					cost: addition.value.cost,
					profitFlag: addition.value.profitFlag,
					name: addition.value.name,
					sign: 1,
					metadata: {
						additionName: addition.value.name,
						itemized: addition.value.itemized,
					},
				},
				parentBucket: bucket.value,
			});

			if (isNewAdjustment.value && fimenu.value.isDealFinalized()) {
				isBusy.value = true;
				await accountingHelper.addAdditionAdjustment(fimenu.value, addition.value);
				isBusy.value = false;
			}

			bucket.value.items.push(foundItem);
		}

		foundItem.profit = addition.value.price - addition.value.cost;

		// Update last edit info
		addition.value.editedById = user.EmployeeId;
		addition.value.editedByName = user.EmployeeName;
		addition.value.lastModifiedAt = new Date();

		if (isNewAddition.value) {
			addition.value.createdById = user.EmployeeId;
			addition.value.createdByName = user.EmployeeName;
			addition.value.createdAt = new Date(addition.value.lastModifiedAt);
		}

		$modal.close(bo.value);
	}
	function cancel() {
		$modal.cancel();
	}

	// Only tax lines get reset when Buyer's Order is reset
	watch(
		() => bucket.value?.taxIndex,
		() => {
			if (bucket.value) bo.value.isManuallyChanged = true;
		},
	);

	const validations = computed(() => ({
		addition: addition.value?.validation(bo.value) ?? {},
	}));

	const v$ = useVuelidate(validations, { addition });
</script>
<style>
	.ModalAddEditAddition .description {
		justify-content: flex-start;
	}

	.ModalAddEditAddition .modal-header .additional-info {
		font-size: 0.75rem;
		color: var(--third-color);
	}
</style>
